import type * as hast from 'hast';

import { BLOCK_OPENAPI, openapi, paragraph } from '@gitbook/doc-core';
import { Block, Fragment, generateKey } from '@gitbook/slate';
import { createOrderIndex } from '@gitbook/util-orderindex';

import { CustomSwaggerBlock, visitAsync } from '../../utils';
import type { HastToGastTransformFn, HastToGastTransformMap } from './types';
import { fromDataAttributes, getCustomAttributes, isElementCustomNodeWithTag } from './utils';

const properties = ['path', 'method', 'baseUrl', 'summary'];
const paramProperties = ['in', 'name', 'type', 'required'];
const responseProperties = ['status', 'description'];

/**
 * Convert a swagger parsed from HTML/Markdown into a swagger node.
 */
const hastToSwagger: HastToGastTransformFn = async (node, eat, context, options) => {
    const attributes = getCustomAttributes(node);

    if (typeof attributes.src === 'string') {
        const ref = await options.resolveFile(attributes.src);
        if (!ref) {
            return [];
        }
        // it's referencing a file
        return [
            openapi.createBlockWithRef({
                ref,
                path: attributes.path,
                method: attributes.method,
                expanded:
                    typeof attributes.expanded !== 'undefined'
                        ? attributes.expanded !== 'false'
                        : undefined,
                fullWidth:
                    typeof attributes.fullWidth !== 'undefined'
                        ? attributes.fullWidth !== 'false'
                        : undefined,
            }),
        ];
    }

    const data = fromDataAttributes(attributes, properties);
    const fragments: Fragment[] = [];

    // fix type of expanded in data
    if (typeof attributes.expanded !== 'undefined') {
        data.expanded = attributes.expanded !== 'false';
    }

    // fix type of fullWidth in data
    if (typeof attributes.fullwidth !== 'undefined') {
        data.fullWidth = attributes.fullwidth !== 'false';
    }

    // get the description
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, CustomSwaggerBlock.description),
        async (description) => {
            let nodes = await eat('blocks', description.children, context, description);

            if (nodes.length === 0) {
                nodes = [paragraph.createParagraph()];
            }

            const fragment = Fragment.create({
                fragment: 'description',
                type: openapi.legacyEditable.FRAGMENT_SWAGGER_OPERATION_DESCRIPTION,
                nodes,
            });

            fragments.push(fragment);
        }
    );

    let lastOrderIndex: string | undefined;

    // get the parameters
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, CustomSwaggerBlock.parameter),
        async (parameter) => {
            if (!data.parameters) {
                data.parameters = {};
            }

            const paramId = generateKey();

            data.parameters[paramId] = {
                ...fromDataAttributes(parameter.properties, paramProperties),
                orderIndex: createOrderIndex(lastOrderIndex ? { before: lastOrderIndex } : {}),
            };

            lastOrderIndex = data.parameters[paramId].orderIndex;

            // fix type of required (get parsed as an empty string if true)
            data.parameters[paramId].required =
                typeof data.parameters[paramId].required !== 'undefined' &&
                // @ts-ignore
                data.parameters[paramId].required !== 'false';

            let nodes = await eat('blocks', parameter.children, context, parameter);

            if (nodes.length === 0) {
                nodes = [paragraph.createParagraph()];
            }

            const fragment = Fragment.create({
                fragment: paramId,
                type: openapi.legacyEditable.FRAGMENT_SWAGGER_PARAMETER_DESCRIPTION,
                nodes,
            });

            fragments.push(fragment);
        }
    );

    lastOrderIndex = undefined;

    // get the responses
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, CustomSwaggerBlock.response),
        async (response) => {
            if (!data.responses) {
                data.responses = {};
            }

            const responseId = generateKey();

            data.responses[responseId] = {
                status: '',
                description: '',
                ...fromDataAttributes(response.properties, responseProperties),
                orderIndex: createOrderIndex(lastOrderIndex ? { before: lastOrderIndex } : {}),
            };

            lastOrderIndex = data.responses[responseId].orderIndex;

            let nodes = await eat('blocks', response.children, context, response);

            if (nodes.length === 0) {
                nodes = [paragraph.createParagraph()];
            }

            const fragment = Fragment.create({
                fragment: responseId,
                type: openapi.legacyEditable.FRAGMENT_SWAGGER_RESPONSE_DESCRIPTION,
                nodes,
            });

            fragments.push(fragment);
        }
    );

    return [
        Block.createVoid({
            type: BLOCK_OPENAPI,
            fragments,
            data,
        }),
    ];
};

/**
 * Convert a GBN api-method parsed from HTML/Markdown into a swagger node.
 */
const legacyHastToSwagger: HastToGastTransformFn = async (node, eat, context) => {
    const attributes = getCustomAttributes(node);
    const parsedData = fromDataAttributes(attributes, ['host', 'path', 'method']);
    const data: Partial<openapi.legacyEditable.BlockSwaggerDataFromScratch> = {};
    if (parsedData.host) {
        data.baseUrl = parsedData.host;
    }
    if (parsedData.path) {
        data.path = parsedData.path;
    }
    if (parsedData.method) {
        data.method = parsedData.method.toLowerCase();
    }
    const fragments: Fragment[] = [];

    // get the summary
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, 'api-method-summary'),
        async (summary) => {
            const nodes = await eat('blocks', summary.children, context);
            data.summary = nodes.map((block) => block.getText()).join('');
        }
    );

    // get the description
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, 'api-method-description'),
        async (description) => {
            const nodes = await eat('blocks', description.children, context, description);

            const fragment = Fragment.create({
                fragment: 'description',
                type: openapi.legacyEditable.FRAGMENT_SWAGGER_OPERATION_DESCRIPTION,
                nodes,
            });

            fragments.push(fragment);
        }
    );

    let lastOrderIndex: string | undefined;

    // get the parameters
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, 'api-method-parameter'),
        async (parameter, _index, parent) => {
            if (!data.parameters) {
                data.parameters = {};
            }

            const paramId = generateKey();

            // @ts-expect-error
            const type = parent.properties.dataTag
                .replace('api-method-', '')
                .replace('-parameters', '');
            data.parameters[paramId] = {
                ...fromDataAttributes(parameter.properties, ['name', 'type', 'required']),
                orderIndex: createOrderIndex(lastOrderIndex ? { before: lastOrderIndex } : {}),
                in: type === 'form-data' ? 'body' : type === 'headers' ? 'header' : type,
            };

            if (typeof data.parameters[paramId].required === 'string') {
                data.parameters[paramId].required = data.parameters[paramId].required === 'true';
            }

            lastOrderIndex = data.parameters[paramId].orderIndex;

            const nodes = await eat('blocks', parameter.children, context, parameter);

            const fragment = Fragment.create({
                fragment: paramId,
                type: openapi.legacyEditable.FRAGMENT_SWAGGER_PARAMETER_DESCRIPTION,
                nodes,
            });

            fragments.push(fragment);
        }
    );

    lastOrderIndex = undefined;

    // get the responses
    await visitAsync<hast.Element>(
        node,
        (child: hast.Element) => isElementCustomNodeWithTag(child, 'api-method-response-example'),
        async (response) => {
            if (!data.responses) {
                data.responses = {};
            }

            const responseId = generateKey();

            data.responses[responseId] = {
                status: `${fromDataAttributes(response.properties, ['httpCode']).httpCode || 200}`,
                description: '',
                orderIndex: createOrderIndex(lastOrderIndex ? { before: lastOrderIndex } : {}),
            };

            lastOrderIndex = data.responses[responseId].orderIndex;

            await visitAsync<hast.Element>(
                response,
                (node: hast.Element) =>
                    node.tagName === 'gb-custom-block' &&
                    attributes.dataTag === 'api-method-response-example-description',
                async (summary) => {
                    const nodes = await eat('blocks', summary.children, context, summary);
                    // @ts-expect-error
                    data.responses[responseId].description = nodes
                        .map((block) => block.getText())
                        .join('');
                }
            );

            const nodes = await eat(
                'blocks',
                response.children.filter(
                    ({ properties }: hast.Element) =>
                        properties &&
                        properties.dataTag !== 'api-method-response-example-description'
                ),
                context,
                response
            );

            const fragment = Fragment.create({
                fragment: responseId,
                type: openapi.legacyEditable.FRAGMENT_SWAGGER_RESPONSE_DESCRIPTION,
                nodes,
            });

            fragments.push(fragment);
        }
    );

    return [
        Block.createVoid({
            type: BLOCK_OPENAPI,
            fragments,
            data,
        }),
    ];
};

/**
 * Custom blocks to transform.
 */
export const transformBlockSwaggerCustoms: HastToGastTransformMap<hast.Element> = {
    [BLOCK_OPENAPI]: hastToSwagger,
    openapi: hastToSwagger,
    /* legacy blocks (from GBN) */
    'api-method': legacyHastToSwagger,
};
